/*
 * The per-user localStorage namespace that six services had each written out
 * for themselves: `valoi-<thing>:<userId>`, a JSON read that swallows a parse
 * failure into a fallback, and a JSON write that logs rather than throws.
 *
 * They were not quite identical, which is the argument for this file rather
 * than against it — two of the six silently dropped a corrupt value where the
 * others kept a fallback, and one logged the write failure while another
 * discarded it. The differences were accidents of when each was written, not
 * decisions.
 *
 * Namespacing by user is not cosmetic: two accounts share a browser, and a
 * shelf list keyed only by name would follow the first user into the second
 * one's session.
 *
 * When these become real endpoints this file has no successor — it disappears
 * with the last caller, which is why it stays this small.
 */
import { logger } from '@/shared/utils/logger';

/** `valoi-shelves:u1` or `valoi-notes:u1:video-9`. */
export function storageKey(prefix: string, ...parts: string[]): string {
  return [prefix, ...parts].join(':');
}

/**
 * Reads and parses, returning `fallback` for anything that is not usable —
 * missing, unparseable, or explicitly null.
 *
 * `validate` is for callers whose fallback is an array or a shaped object: JSON
 * round-trips whatever was written, including a value from an older version of
 * the app, so "it parsed" is not the same as "it is the right shape".
 *
 * Be clear about how far that goes: `T` is an assertion, not a proof. `validate`
 * gates the container — that this is an array at all, rather than the object a
 * previous schema stored there — and nothing checks the elements. `[1, 2, 3]`
 * satisfies `readJson<Shelf[]>(…, isArray)`. A caller whose stored shape can
 * actually drift has to filter after reading, the way `shelfService` does; the
 * alternative is a runtime schema, which is a great deal of machinery for six
 * keys that are placeholders for real endpoints.
 */
export function readJson<T>(key: string, fallback: T, validate?: (value: unknown) => boolean): T {
  // No localStorage outside a browser. Every caller is a service that a test
  // may import in a bare node environment.
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || parsed === undefined) return fallback;
    if (validate && !validate(parsed)) return fallback;
    return parsed as T;
  } catch {
    return fallback;
  }
}

/**
 * Writes, or logs and carries on.
 *
 * `label` names the thing in the log line, because "Failed to persist" on its
 * own tells you nothing at three in the morning. A quota error or Safari's
 * private mode must not take a page down over a preference.
 */
export function writeJson(key: string, value: unknown, label: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    logger.error(`Failed to persist ${label}`, err);
  }
}

/** Removing a key that was never set is not an error, and neither is failing to. */
export function removeKey(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to do — the value is already unreadable to us */
  }
}

/**
 * `readJson`'s array guard, which four of the six callers want.
 *
 * A type predicate rather than a plain boolean so it narrows for anyone calling
 * it directly. It still satisfies `readJson`'s `validate` parameter — a
 * predicate is assignable where a boolean-returning function is expected — so
 * no call site changes, and none of them gain a guarantee they did not have.
 */
export const isArray = (value: unknown): value is unknown[] => Array.isArray(value);
